import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Icon,
  Input,
  OtpInput,
  PasswordInput,
  Space,
  Steps,
  Text,
  useToast,
} from "@app/ui";
import { api } from "../api/client.js";
import { QrSvg } from "../components/PassportQr.js";
import { useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";

/**
 * Two-factor tab. Enrollment is the flow that used to make people leave the app:
 * the API hands back an otpauth:// URI, so render it as a scannable code and keep
 * the shared secret as the manual fallback rather than the primary instruction.
 *
 * The code comes from the app's shared `QrSvg` (uqr) — @app/ui's hand-rolled
 * `generateQR` writes bad format-info bits and its output does not decode, so a
 * QR built with it would fail silently in exactly the place it must not.
 */

export function TwoFactorTab({ enabled, onChange }: { enabled: boolean; onChange: () => Promise<void> }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();

  const [enroll, setEnroll] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const beginEnroll = async () => {
    setBusy(true);
    try {
      setEnroll(await api<{ secret: string; otpauthUri: string }>("/auth/mfa/enroll", { method: "POST" }));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (value: string) => {
    setBusy(true);
    try {
      await api<void>("/auth/mfa/enroll/confirm", { method: "POST", body: { code: value } });
      setEnroll(null);
      setCode("");
      toast.success(t("dashboard.mfaDone"));
      await onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api<void>("/auth/mfa/disable", {
        method: "POST",
        body: { password: disablePw, code: disableCode },
      });
      setDisablePw("");
      setDisableCode("");
      toast.success(t("dashboard.mfaDisabled"));
      await onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (enabled) {
    return (
      <Space direction="vertical" size="lg" className="w-full">
        <Alert type="success">{t("profile.mfaActive")}</Alert>
        <Card title={t("profile.tab2fa")}>
          <Space direction="vertical" size="md" className="w-full">
            <Text secondary>{t("profile.mfaActiveHint")}</Text>
            <div className="pf-form">
              <Text small secondary>
                {t("profile.mfaDisableHint")}
              </Text>
              <PasswordInput
                label={t("profile.password")}
                autoComplete="current-password"
                value={disablePw}
                onChange={(e) => setDisablePw(e.target.value)}
              />
              <Input
                label={t("profile.mfaCode")}
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <div>
                <Button
                  variant="danger"
                  size="sm"
                  loading={busy}
                  disabled={!disablePw || disableCode.length < 6}
                  onClick={() => void disable()}
                >
                  {t("profile.mfaDisable")}
                </Button>
              </div>
            </div>
          </Space>
        </Card>
      </Space>
    );
  }

  return (
    <Card title={t("dashboard.mfaTitle")}>
      <Space direction="vertical" size="md" className="w-full">
        <Steps
          current={enroll ? 1 : 0}
          items={[
            { title: t("dashboard.mfaStepStart") },
            { title: t("dashboard.mfaStepVerify") },
            { title: t("dashboard.mfaStepEnabled") },
          ]}
        />
        {!enroll ? (
          <>
            <Text secondary>{t("profile.mfaOffHint")}</Text>
            <div>
              <Button
                variant="primary"
                loading={busy}
                icon={<Icon name="shield" size={14} />}
                onClick={() => void beginEnroll()}
              >
                {t("dashboard.mfaStart")}
              </Button>
            </div>
          </>
        ) : (
          <div className="pf-enroll">
            <div className="pf-qr">
              <QrSvg text={enroll.otpauthUri} size={176} />
            </div>
            <div className="pf-enroll__steps">
              <Text secondary>{t("profile.mfaScanHint")}</Text>
              <div>
                <Text small secondary>
                  {t("profile.mfaManualHint")}
                </Text>
                <div className="mt-1">
                  <Text code copyable small className="pf-secret">
                    {enroll.secret}
                  </Text>
                </div>
              </div>
              <OtpInput
                label={t("profile.mfaCode")}
                value={code}
                onInput={setCode}
                onComplete={(v) => void confirmEnroll(v)}
                disabled={busy}
                autoFocus
              />
              <div>
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={code.length < 6}
                  icon={<Icon name="check" size={14} />}
                  onClick={() => void confirmEnroll(code)}
                >
                  {t("dashboard.mfaConfirm")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
}
